import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CheckCircle2, AlertCircle, Loader2, FileText, Upload, X } from 'lucide-react';
import { getLanguageFromPath } from '../lib/i18n';

export default function ContactForm({ 
  source = 'Generel kontakt', 
  defaultService, 
  buttonText, 
  title,
  isErhvervsleasing = false,
  lang
}) {
  const location = useLocation();
  const currentLang = lang || getLanguageFromPath(location.pathname);

  const I18N = {
    da: {
      nameLabel: 'Navn',
      namePlaceholder: 'F.eks. Mette Hansen',
      nameRequired: 'Navn er påkrævet',
      phoneLabel: 'Telefonnummer',
      phonePlaceholder: 'F.eks. 12 34 56 78',
      phoneRequired: 'Telefonnummer er påkrævet',
      phoneInvalid: 'Indtast et gyldigt telefonnummer',
      emailLabel: 'E-mail',
      emailPlaceholder: 'F.eks. mette@eksempel.dk',
      emailRequired: 'E-mail er påkrævet',
      emailInvalid: 'Indtast en gyldig e-mailadresse',
      descLabel: 'Besked / henvendelse',
      descPlaceholder: 'Fortæl os hvad du søger eller gerne vil vide mere om...',
      descRequired: 'Beskrivelse er påkrævet',
      uploadTitle: 'Upload seneste årsregnskab',
      uploadHelp: 'PDF, DOC, DOCX, XLS eller XLSX (Maks 10 MB)',
      uploadButton: 'Vælg fil fra computer',
      uploadRequired: 'Du skal uploade virksomhedens seneste årsregnskab.',
      uploadInvalidExt: 'Filen skal være i formatet PDF, DOC, DOCX, XLS eller XLSX.',
      uploadTooLarge: 'Filen må maksimalt fylde 10 MB.',
      termsText: 'Jeg bekræfter, at jeg har læst og accepterer privatlivspolitikken samt fremsendelse af regnskabsdata til kreditvurdering.',
      termsRequired: 'Du skal bekræfte, at du har læst privatlivspolitikken, før ansøgningen kan sendes.',
      defaultBtn: 'Send besked',
      sendingBtn: 'Sender besked...',
      successTitle: 'Tak for din henvendelse!',
      successText: 'Vi har modtaget din besked og vender tilbage til dig hurtigst muligt – typisk inden for 24 timer.',
      sendAnother: 'Send en ny besked',
      errorTitle: 'Der opstod en fejl',
      errorText: 'Beklager, din besked kunne ikke sendes automatisk. Kontakt os venligst direkte på telefon +45 2530 3589 eller e-mail info@danshaker.dk.',
      tryAgain: 'Prøv igen'
    },
    en: {
      nameLabel: 'Name',
      namePlaceholder: 'E.g. John Smith',
      nameRequired: 'Name is required',
      phoneLabel: 'Phone number',
      phonePlaceholder: 'E.g. +44 123 456 789',
      phoneRequired: 'Phone number is required',
      phoneInvalid: 'Please enter a valid phone number',
      emailLabel: 'Email',
      emailPlaceholder: 'E.g. john@example.com',
      emailRequired: 'Email is required',
      emailInvalid: 'Please enter a valid email address',
      descLabel: 'Message / inquiry',
      descPlaceholder: 'Tell us about your stable or what you would like to know...',
      descRequired: 'Message is required',
      uploadTitle: 'Upload latest annual report',
      uploadHelp: 'PDF, DOC, DOCX, XLS or XLSX (Max 10 MB)',
      uploadButton: 'Choose file from device',
      uploadRequired: 'Please upload the company’s latest financial report.',
      uploadInvalidExt: 'File must be in PDF, DOC, DOCX, XLS or XLSX format.',
      uploadTooLarge: 'File must not exceed 10 MB.',
      termsText: 'I confirm that I have read and accept the privacy policy and submission of financial data for credit assessment.',
      termsRequired: 'You must accept the terms before submitting.',
      defaultBtn: 'Send message',
      sendingBtn: 'Sending message...',
      successTitle: 'Thank you for your message!',
      successText: 'We have received your inquiry and will get back to you as soon as possible – typically within 24 hours.',
      sendAnother: 'Send another message',
      errorTitle: 'An error occurred',
      errorText: 'Sorry, your message could not be sent. Please contact us directly at +45 2530 3589 or info@danshaker.dk.',
      tryAgain: 'Try again'
    },
    de: {
      nameLabel: 'Name',
      namePlaceholder: 'Z.B. Michael Schmidt',
      nameRequired: 'Name ist erforderlich',
      phoneLabel: 'Telefonnummer',
      phonePlaceholder: 'Z.B. +49 151 12345678',
      phoneRequired: 'Telefonnummer ist erforderlich',
      phoneInvalid: 'Bitte geben Sie eine gültige Telefonnummer ein',
      emailLabel: 'E-Mail-Adresse',
      emailPlaceholder: 'Z.B. michael@beispiel.de',
      emailRequired: 'E-Mail ist erforderlich',
      emailInvalid: 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
      descLabel: 'Nachricht / Anfrage',
      descPlaceholder: 'Erzählen Sie uns von Ihrem Stall oder Ihren Fragen...',
      descRequired: 'Nachricht ist erforderlich',
      uploadTitle: 'Aktuellen Jahresabschluss hochladen',
      uploadHelp: 'PDF, DOC, DOCX, XLS oder XLSX (Max. 10 MB)',
      uploadButton: 'Datei vom Gerät auswählen',
      uploadRequired: 'Bitte laden Sie den aktuellen Jahresabschluss hoch.',
      uploadInvalidExt: 'Die Datei muss im Format PDF, DOC, DOCX, XLS oder XLSX sein.',
      uploadTooLarge: 'Die Datei darf maximal 10 MB groß sein.',
      termsText: 'Ich bestätige, dass ich die Datenschutzerklärung gelesen habe und der Datenverarbeitung zur Bonitätsprüfung zustimme.',
      termsRequired: 'Sie müssen der Datenverarbeitung zustimmen, bevor Sie fortfahren.',
      defaultBtn: 'Nachricht senden',
      sendingBtn: 'Nachricht wird gesendet...',
      successTitle: 'Vielen Dank für Ihre Anfrage!',
      successText: 'Wir haben Ihre Nachricht erhalten und melden uns schnellstmöglich bei Ihnen – in der Regel innerhalb von 24 Stunden.',
      sendAnother: 'Weitere Nachricht senden',
      errorTitle: 'Ein Fehler ist aufgetreten',
      errorText: 'Entschuldigung, Ihre Nachricht konnte nicht übermittelt werden. Bitte kontaktieren Sie uns direkt unter +45 2530 3589 oder info@danshaker.dk.',
      tryAgain: 'Erneut versuchen'
    }
  };

  const t = I18N[currentLang] || I18N.da;
  const activeBtnText = buttonText || t.defaultBtn;
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '' // Honeypot field
  });

  const [file, setFile] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errors, setErrors] = useState({});

  const checkErhvervsleasing = isErhvervsleasing || defaultService === 'Erhvervsleasing' || title === 'Ansøg om erhvervsleasing' || source === 'Erhvervsleasing';

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = t.nameRequired;
    if (!formData.phone.trim()) {
      newErrors.phone = t.phoneRequired;
    } else if (!/^[0-9\s+]{8,15}$/.test(formData.phone.trim())) {
      newErrors.phone = t.phoneInvalid;
    }
    if (!formData.email.trim()) {
      newErrors.email = t.emailRequired;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t.emailInvalid;
    }
    if (!formData.description.trim()) newErrors.description = t.descRequired;
    
    if (checkErhvervsleasing) {
      // Validering af årsregnskab filupload
      if (!file) {
        newErrors.file = t.uploadRequired;
      } else {
        const allowedExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx'];
        const ext = file.name.split('.').pop()?.toLowerCase() || '';
        if (!allowedExtensions.includes(ext)) {
          newErrors.file = t.uploadInvalidExt;
        } else if (file.size > 10 * 1024 * 1024) { // 10 MB
          newErrors.file = t.uploadTooLarge;
        }
      }

      // Validering af bekræftelsesfelt
      if (!acceptedTerms) {
        newErrors.acceptedTerms = t.termsRequired;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setFile(selectedFile);
      if (errors.file) {
        setErrors(prev => ({ ...prev, file: '' }));
      }
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setStatus('loading');

    try {
      const payload = new FormData();
      payload.append('name', formData.name);
      payload.append('phone', formData.phone);
      payload.append('email', formData.email);
      payload.append('description', formData.description);
      payload.append('website_url', formData.website_url);
      payload.append('source', source || defaultService || 'Generel kontakt');
      payload.append('submittedAt', new Date().toISOString());

      if (checkErhvervsleasing) {
        if (file) {
          payload.append('aarsregnskab', file);
        }
        payload.append('accepted_terms', acceptedTerms ? 'true' : 'false');
      }

      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        body: payload,
      });

      if (response.ok) {
        setStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: ''
        });
        setFile(null);
        setAcceptedTerms(false);
      } else {
        throw new Error('Submission failed');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setStatus('error');
    }
  };

  return (
    <div className="w-full rounded-2xl border border-slate-200 bg-white p-6 shadow-xl md:p-8">
      {title && (
        <h2 className="text-2xl font-black text-slate-950 mb-4">{title}</h2>
      )}
      {status === 'success' ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="mb-4 rounded-full bg-[#0E67A2]/10 p-3 text-[#0E67A2]">
            <CheckCircle2 className="h-12 w-12" />
          </div>
          <h3 className="mb-2 text-2xl font-bold text-slate-900">
            {checkErhvervsleasing
              ? (currentLang === 'en' ? 'Thank you for your commercial leasing application!' : currentLang === 'de' ? 'Vielen Dank für Ihre Gewerbeleasing-Anfrage!' : 'Tak for din erhvervsleasing-ansøgning!')
              : t.successTitle}
          </h3>
          <p className="max-w-md text-slate-600 font-medium leading-relaxed">
            {checkErhvervsleasing
              ? (currentLang === 'en'
                  ? 'We have received your annual report along with your application. The document is treated confidentially and used to assess your commercial leasing application.'
                  : currentLang === 'de'
                  ? 'Wir haben Ihren Jahresabschluss zusammen mit Ihrer Anfrage erhalten. Das Dokument wird vertraulich behandelt und zur Bonitätsprüfung herangezogen.'
                  : 'Vi har modtaget dit årsregnskab sammen med din ansøgning. Dokumentet behandles fortroligt og anvendes til vurdering af din ansøgning om erhvervsleasing.')
              : t.successText}
          </p>
          <button
            onClick={() => setStatus('idle')}
            className="mt-6 rounded-xl bg-slate-100 px-6 py-2.5 font-semibold text-slate-800 transition hover:bg-slate-200 cursor-pointer"
          >
            {t.sendAnother}
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Honeypot field - ABSOLUTELY HIDDEN from humans */}
          <div className="sr-only absolute -top-[9999px] -left-[9999px] h-0 w-0 overflow-hidden">
            <label htmlFor="website_url">Do not fill this field</label>
            <input
              id="website_url"
              type="text"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              autoComplete="off"
            />
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-slate-700">
                {t.nameLabel} <span className="text-[#0E67A2]">*</span>
              </label>
              <input
                id="name"
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder={t.namePlaceholder}
                className={`w-full rounded-xl border bg-white px-4 py-3 text-slate-900 placeholder-slate-400 outline-none transition focus:border-[#0E67A2]/50 focus:ring-2 focus:ring-[#0E67A2]/10 ${
                  errors.name ? 'border-red-500' : 'border-slate-200'
                }`}
              />
              {errors.name && (
                <p className="mt-1 flex items-center gap-1 text-xs text-red-500">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {errors.name}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="phone" className="mb-1.5 block text-sm font-medium text-slate-700">
                {t.phoneLabel} <span className="text-[#0E67A2]">*</span>
              </label>
              <input
                id="phone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder={t.phonePlaceholder}
                className={`w-full rounded-xl border bg-white px-4 py-3 text-slate-900 placeholder-slate-400 outline-none transition focus:border-[#0E67A2]/50 focus:ring-2 focus:ring-[#0E67A2]/10 ${
                  errors.phone ? 'border-red-500' : 'border-slate-200'
                }`}
              />
              {errors.phone && (
                <p className="mt-1 flex items-center gap-1 text-xs text-red-500">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {errors.phone}
                </p>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-slate-700">
              {t.emailLabel} <span className="text-[#0E67A2]">*</span>
            </label>
            <input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={t.emailPlaceholder}
              className={`w-full rounded-xl border bg-white px-4 py-3 text-slate-900 placeholder-slate-400 outline-none transition focus:border-[#0E67A2]/50 focus:ring-2 focus:ring-[#0E67A2]/10 ${
                errors.email ? 'border-red-500' : 'border-slate-200'
              }`}
            />
            {errors.email && (
              <p className="mt-1 flex items-center gap-1 text-xs text-red-500">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {errors.email}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="description" className="mb-1.5 block text-sm font-medium text-slate-700">
              {t.descLabel} <span className="text-[#0E67A2]">*</span>
            </label>
            <textarea
              id="description"
              name="description"
              rows="4"
              value={formData.description}
              onChange={handleChange}
              placeholder={t.descPlaceholder}
              className={`w-full rounded-xl border bg-white px-4 py-3 text-slate-900 placeholder-slate-400 outline-none transition focus:border-[#0E67A2]/50 focus:ring-2 focus:ring-[#0E67A2]/10 ${
                errors.description ? 'border-red-500' : 'border-slate-200'
              }`}
            />
            {errors.description && (
              <p className="mt-1 flex items-center gap-1 text-xs text-red-500">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {errors.description}
              </p>
            )}
          </div>

          {/* Upload af årsregnskab felt til Erhvervsleasing */}
          {checkErhvervsleasing && (
            <div className="space-y-2 pt-2 border-t border-slate-100">
              <div>
                <label htmlFor="aarsregnskab" className="block text-sm font-bold text-slate-900">
                  {t.uploadTitle} <span className="text-[#0E67A2]">*</span>
                </label>
                <p className="mt-1 text-xs text-slate-600 font-medium">
                  {t.uploadHelp}
                </p>
              </div>

              <div className="relative pt-1">
                <input
                  id="aarsregnskab"
                  type="file"
                  name="aarsregnskab"
                  accept=".pdf,.doc,.docx,.xls,.xlsx"
                  onChange={handleFileChange}
                  className="hidden"
                />
                
                {file ? (
                  <div className="flex items-center justify-between rounded-xl border border-slate-300 bg-slate-50 p-3.5">
                    <div className="flex items-center gap-3 overflow-hidden">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#0E67A2]/10 text-[#0E67A2]">
                        <FileText className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{file.name}</p>
                        <p className="text-xs text-slate-500 font-medium">{(file.size / (1024 * 1024)).toFixed(2)} MB</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemoveFile}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-200 hover:text-slate-700 transition"
                      title="Fjern fil"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <label
                    htmlFor="aarsregnskab"
                    className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-5 text-center transition hover:border-[#0E67A2] hover:bg-slate-50 ${
                      errors.file ? 'border-red-500 bg-red-50/20' : 'border-slate-300 bg-slate-50/50'
                    }`}
                  >
                    <Upload className="h-8 w-8 text-[#0E67A2] mb-2" />
                    <span className="text-sm font-semibold text-slate-900">{t.uploadButton}</span>
                    <span className="mt-1 text-xs text-slate-500 font-medium">
                      {t.uploadHelp}
                    </span>
                  </label>
                )}
              </div>

              {errors.file && (
                <p className="flex items-center gap-1 text-xs font-semibold text-red-500 pt-1">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {errors.file}
                </p>
              )}

              <p className="mt-3 text-xs leading-relaxed text-slate-600 font-medium border-l-2 border-[#0E67A2] pl-3 py-0.5">
                {currentLang === 'en'
                  ? 'Your financial report is treated confidentially and only used to assess your commercial leasing application in accordance with our privacy policy.'
                  : currentLang === 'de'
                  ? 'Ihr Jahresabschluss wird vertraulich behandelt und ausschließlich zur Prüfung Ihres Leasingantrags gemäß unserer Datenschutzerklärung verwendet.'
                  : 'Dit årsregnskab behandles fortroligt og anvendes udelukkende til at behandle og vurdere din ansøgning om erhvervsleasing. Dokumentet opbevares kun, så længe det er nødvendigt for behandlingen af din ansøgning, medmindre virksomheden er juridisk forpligtet til at opbevare det længere. Du kan læse mere i vores privatlivspolitik.'}
              </p>
            </div>
          )}

          {/* Acceptfelt til Erhvervsleasing */}
          {checkErhvervsleasing && (
            <div className="pt-2">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  name="accepted_terms"
                  checked={acceptedTerms}
                  onChange={(e) => {
                    setAcceptedTerms(e.target.checked);
                    if (errors.acceptedTerms) {
                      setErrors(prev => ({ ...prev, acceptedTerms: '' }));
                    }
                  }}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#0E67A2] focus:ring-[#0E67A2]/20 cursor-pointer"
                />
                <span className="text-xs text-slate-700 font-medium leading-normal group-hover:text-slate-900">
                  {t.termsText} <span className="text-[#0E67A2]">*</span>
                </span>
              </label>
              {errors.acceptedTerms && (
                <p className="mt-1.5 flex items-center gap-1 text-xs font-semibold text-red-500">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {errors.acceptedTerms}
                </p>
              )}
            </div>
          )}

          {status === 'error' && (
            <div className="flex items-center gap-2 rounded-xl border border-red-500/20 bg-red-50/50 p-4 text-sm text-red-600 font-medium">
              <AlertCircle className="h-5 w-5 shrink-0" />
              <p>{t.errorText}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'loading'}
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#0E67A2] px-6 py-3.5 text-base font-bold text-white transition hover:bg-[#0E67A2]/90 disabled:opacity-50 cursor-pointer shadow-md"
          >
            {status === 'loading' ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>{t.sendingBtn}</span>
              </>
            ) : (
              <span>{activeBtnText}</span>
            )}
          </button>
        </form>
      )}
    </div>
  );
}

